import * as React from "react";
import { t } from "i18next";
import { CheckOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { ZUid } from "src/types/zUid";
import { CurrentNodePort } from "src/pages/ManagementPage/objects/ports";
import {
  ActionName,
  ZPermissions,
  ZRolesGroup,
  ZTypeActions,
} from "./roleTypes";
import styles from "./RolesEditor.module.less";
import { createActionsSet, fromActionsSet, makeActionKey } from "./actionsSet";
import { CopyOnRoleButton } from "./CopyOnRoleButton";

/* eslint jsx-a11y/control-has-associated-label: "off" */

interface PropsRolesEditor {
  columns: ZTypeActions[];
  rows: ZRolesGroup[];
  store: CurrentNodePort;
  stateId?: ZUid;
  canCopy?: boolean;
  value?: ZPermissions;
  onChange?(value?: ZPermissions): void;
}

const actionNameMap: Partial<Record<ActionName, string>> = {
  [ActionName.view]: "READ",
};

export const actionName = (rawName: string): string => {
  const chunks = rawName.split("_");
  if (chunks.length <= 1) return rawName;
  const result = chunks.slice(1).join("_");
  return actionNameMap[result as ActionName] ?? result;
};

export const RolesEditor: React.FC<PropsRolesEditor> = (props) => {
  const { columns, rows, stateId, canCopy, value, onChange, store } = props;
  const flatActions = columns.flatMap(({ actions }) => actions);
  const actionsSet = createActionsSet(value ?? []);
  const onClick = (roleId: ZUid, actionId: ZUid) => {
    const key = makeActionKey(roleId, actionId);
    const newSet = new Set(actionsSet);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    onChange?.(fromActionsSet(newSet, columns, rows));
  };

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th />
            {columns.map(({ type, actions }) => (
              <th key={type} colSpan={actions.length}>
                {t(`actionType.${type}`)}
              </th>
            ))}
          </tr>
          <tr>
            <th />
            {flatActions.map(({ id, name }) => (
              <th key={id}>{actionName(name)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ groupId, groupName, roles }) => (
            <React.Fragment key={groupId}>
              <tr>
                <th className={styles.groupName}>{groupName}</th>
                {flatActions.map(({ id }) => (
                  <td key={id} className={styles.group} />
                ))}
              </tr>
              {roles.map(({ roleId, roleName }) => (
                <tr key={roleId}>
                  <th className={styles.roleName}>
                    <Flex align="center" justify="space-between" gap={12}>
                      {roleName}
                      {canCopy && stateId && (
                        <CopyOnRoleButton
                          stateId={stateId}
                          roleName={roleName}
                          roleId={roleId}
                          groupId={groupId}
                          store={store}
                        />
                      )}
                    </Flex>
                  </th>
                  {flatActions.map(({ id, name }) => {
                    const checked = actionsSet.has(makeActionKey(roleId, id));
                    return (
                      <td key={id}>
                        <button
                          type="button"
                          className={styles.cell}
                          onClick={() => onClick(roleId, id)}
                          data-testid={`${roleName}::${name}`}
                          data-checked={checked}
                        >
                          {checked ? <CheckOutlined /> : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
